import { useState, useEffect } from "react";
import Dialog from "../../designSystem/c-organism/dialogs/Dialog";
import InputFile from "../../designSystem/b-molecules/formElements/input/InputFile";
import Button from "../../designSystem/b-molecules/buttons/Button";
import Select from "../../designSystem/b-molecules/formElements/select/Select";
import Input from "../../designSystem/b-molecules/formElements/input/Input";
import Papa from "papaparse";
import { ref, push, get } from "firebase/database";
import { db } from "../../configs/Firebase";
import { getAllRecords, filterDuplicates } from "./utils/processData";
import style from "./UploadDialog.module.css";

const UploadDialog = ({ show, onClose, orgId, userId, onUploadComplete, brands = [], countries = [], sources = [] }) => {
  const [file, setFile] = useState(null);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [parsedData, setParsedData] = useState([]);
  const [source, setSource] = useState("customer_service");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCountry, setSelectedCountry] = useState("");
  const [columnMapping, setColumnMapping] = useState({
    feedbackText: "",
    date: "",
    rating: "",
    userId: ""
  });
  const [error, setError] = useState("");
  const [uploading, setUploading] = useState(false);

  const defaultSourceOptions = [
    { value: "customer_service", label: "Customer Service" },
    { value: "google_play", label: "Google Play" },
    { value: "app_store", label: "App Store" },
    { value: "other", label: "Other" }
  ];

  const requiredFields = [
    { key: "date", label: "Date" }
  ];

  const optionalFields = [
    { key: "feedbackText", label: "Feedback Text" },
    { key: "rating", label: "Rating" },
    { key: "userId", label: "User ID" }
  ];

  useEffect(() => {
    if (!show) {
      // Reset state when dialog closes
      setFile(null);
      setCsvHeaders([]);
      setParsedData([]);
      setSelectedBrand("");
      setSelectedCountry("");
      setColumnMapping({
        feedbackText: "",
        date: "",
        rating: "",
        userId: ""
      });
      setError("");
    }
  }, [show]);

  function handleFileChange(selectedFile) {
    setFile(selectedFile);
    setError("");

    if (!selectedFile) return;

    const reader = new FileReader();
    reader.onload = ({ target }) => {
      try {
        const csv = Papa.parse(target.result, { 
          header: true,
          skipEmptyLines: true
        });
        
        if (csv.errors && csv.errors.length > 0) {
          setError("Error parsing CSV: " + csv.errors[0].message);
          return;
        }

        const data = csv.data.filter(row => {
          // Filter out completely empty rows
          return Object.values(row).some(val => val && val.toString().trim() !== '');
        });

        if (data.length === 0) {
          setError("CSV file appears to be empty");
          return;
        }

        setParsedData(data);
        setCsvHeaders(Object.keys(data[0] || {}));
      } catch (err) {
        setError("Error reading file: " + err.message);
      }
    };
    reader.readAsText(selectedFile);
  }

  function handleSourceChange(e, index, options) {
    const opts = (options && options.length) ? options : defaultSourceOptions;
    setSource(opts[index].value);
  }

  function handleColumnMappingChange(field, value) {
    setColumnMapping(prev => ({
      ...prev,
      [field]: value
    }));
  }

  function validateMapping() {
    // Brand is required
    if (!selectedBrand) {
      setError("Please select a brand");
      return false;
    }
    // Country is required
    if (!selectedCountry) {
      setError("Please select a country");
      return false;
    }
    // Date is always required
    if (!columnMapping.date) {
      setError("Please map the Date column");
      return false;
    }
    // Feedback text is optional if rating is provided
    // But at least one of feedbackText or rating should be mapped for meaningful data
    if (!columnMapping.feedbackText && !columnMapping.rating) {
      setError("Please map either the Feedback Text column or Rating column (or both)");
      return false;
    }
    return true;
  }

  async function handleUpload() {
    if (!file) {
      setError("Please select a CSV file");
      return;
    }

    if (!validateMapping()) {
      return;
    }

    setUploading(true);
    setError("");

    try {
      // First, fetch existing uploads to check for duplicates
      const uploadsRef = ref(db, `userData/${orgId}/customers/data`);
      const existingUploadsSnapshot = await get(uploadsRef);
      const existingUploadsData = existingUploadsSnapshot.val();
      const existingUploads = existingUploadsData 
        ? Object.keys(existingUploadsData).map(id => ({
            id,
            ...existingUploadsData[id]
          }))
        : [];
      
      // Get all existing records
      const existingRecords = getAllRecords(existingUploads);

      // Process new records from CSV (without IDs initially)
      const recordsArray = [];
      parsedData.forEach((row, index) => {
        const record = {
          feedbackText: row[columnMapping.feedbackText] || "",
          date: row[columnMapping.date] || "",
          source: source,
          brand: selectedBrand,
          country: selectedCountry,
        };

        if (columnMapping.rating && row[columnMapping.rating]) {
          record.rating = row[columnMapping.rating];
        }

        if (columnMapping.userId && row[columnMapping.userId]) {
          record.userId = row[columnMapping.userId];
        }

        // Store record if it has a valid date (required field)
        // This ensures records with ratings but no text are still stored for accurate rating calculations
        if (record.date && record.date.trim() !== '') {
          recordsArray.push(record);
        }
      });

      if (recordsArray.length === 0) {
        setError("No valid records found after mapping");
        setUploading(false);
        return;
      }

      // Check for duplicates
      const { newRecords, duplicates, summary } = filterDuplicates(recordsArray, existingRecords);

      // Show summary if there are duplicates
      if (duplicates.length > 0) {
        const message = `Found ${summary.duplicates} duplicate record(s) that will be skipped. ${summary.new} new record(s) will be uploaded. Continue?`;
        if (!window.confirm(message)) {
          setUploading(false);
          return;
        }
      }

      // Only upload new records
      if (newRecords.length === 0) {
        setError("All records are duplicates. Nothing to upload.");
        setUploading(false);
        return;
      }

      // Process new records with IDs
      const recordsObj = {};
      newRecords.forEach((record, index) => {
        const recordId = `${Date.now()}-${index}`;
        recordsObj[recordId] = {
          id: recordId,
          ...record,
          theme: null,
          themeName: null
        };
      });

      // Safety check: ensure we have records to upload
      if (Object.keys(recordsObj).length === 0) {
        setError("No records to upload after duplicate filtering.");
        setUploading(false);
        return;
      }

      // Store in Firebase
      const uploadData = {
        source: source || "customer_service", // Ensure source is always set
        uploadedAt: { ".sv": "timestamp" },
        uploadedBy: userId,
        columnMapping: columnMapping,
        recordCount: newRecords.length,
        records: recordsObj  // Store as object for easier updates
      };

      const uploadRef = ref(db, `userData/${orgId}/customers/data`);
      const snapshot = await push(uploadRef, uploadData);
      const uploadId = snapshot.key;

      // Trigger categorization callback
      if (onUploadComplete) {
        onUploadComplete(uploadId, Object.values(recordsObj));
      }

      // Close dialog
      onClose();
    } catch (err) {
      setError("Error uploading data: " + err.message);
      setUploading(false);
    }
  }

  return (
    <Dialog show={show} onClose={onClose} title="Upload Customer Feedback" variants={["large"]}>
      <div className={style.uploadDialog}>
        <InputFile
          label="Choose CSV File"
          onChange={handleFileChange}
          error={error && !csvHeaders.length ? error : ""}
        />

        {csvHeaders.length > 0 && (
          <>
            <div className={style.sourceSelector}>
              {(() => {
                const effectiveSourceOptions =
                  sources && sources.length > 0
                    ? sources.map(s => ({ value: s.key, label: s.name }))
                    : defaultSourceOptions;

                const currentSourceOption =
                  effectiveSourceOptions.find(opt => opt.value === source) ||
                  effectiveSourceOptions[0];

                return (
                  <Select
                    label="Source"
                    type="dropdown"
                    options={effectiveSourceOptions.map(opt => ({ value: opt.label }))}
                    selectedOptions={[currentSourceOption ? currentSourceOption.label : ""]}
                    onChange={(e, index) => handleSourceChange(e, index, effectiveSourceOptions)}
                    multiselect={false}
                  />
                );
              })()}
            </div>

            <div className={style.brandSelector}>
              <Select
                label="Brand"
                type="dropdown"
                options={brands.length > 0 ? brands.map(brand => ({ value: brand.name })) : [{ value: "No brands available" }]}
                selectedOptions={[brands.find(b => b.id === selectedBrand)?.name || ""]}
                onChange={(e, index) => {
                  if (brands.length > 0) {
                    setSelectedBrand(brands[index].id);
                  }
                }}
                multiselect={false}
                error={!selectedBrand && csvHeaders.length > 0 ? "Brand is required" : ""}
              />
              {brands.length === 0 && (
                <p className={style.helpText} style={{ fontSize: '12px', color: 'var(--on--400)', marginTop: 'var(--padding--xsmall)' }}>
                  No brands available. Please create a brand first.
                </p>
              )}
            </div>

            <div className={style.countrySelector}>
              <Select
                label="Country"
                type="dropdown"
                options={countries.length > 0 ? countries.map(country => ({ value: country.name })) : [{ value: "No countries available" }]}
                selectedOptions={[countries.find(c => c.id === selectedCountry)?.name || ""]}
                onChange={(e, index) => {
                  if (countries.length > 0) {
                    setSelectedCountry(countries[index].id);
                  }
                }}
                multiselect={false}
                error={!selectedCountry && csvHeaders.length > 0 ? "Country is required" : ""}
              />
              {countries.length === 0 && (
                <p className={style.helpText} style={{ fontSize: '12px', color: 'var(--on--400)', marginTop: 'var(--padding--xsmall)' }}>
                  No countries available. Please create a country first.
                </p>
              )}
            </div>

            <div className={style.mappingSection}>
              <h4>Map CSV Columns</h4>
              <p className={style.helpText}>
                Select which columns in your CSV file correspond to each field.
              </p>

              {requiredFields.map(field => (
                <div key={field.key} className={style.mappingRow}>
                  {/* <label>{field.label} <span className={style.required}>*</span></label> */}
                  <Select
                    label={field.label + " *"}
                    type="dropdown"
                    options={csvHeaders.map(header => ({ value: header }))}
                    selectedOptions={[columnMapping[field.key] || ""]}
                    onChange={(e, index) => handleColumnMappingChange(field.key, csvHeaders[index])}
                    multiselect={false}
                  />
                </div>
              ))}
              
              <div className={style.helpText} style={{ marginTop: 'var(--padding--small)', fontSize: '12px', color: 'var(--on--400)' }}>
                Note: At least one of Feedback Text or Rating must be mapped for meaningful data.
              </div>

              {optionalFields.map(field => (
                <div key={field.key} className={style.mappingRow}>
                  {/* <label>{field.label}</label> */}
                  <Select
                    label={field.label}
                    type="dropdown"
                    options={[{ value: "None" }, ...csvHeaders.map(header => ({ value: header }))]}
                    selectedOptions={[columnMapping[field.key] || "None"]}
                    onChange={(e, index) => {
                      const value = index === 0 ? "" : csvHeaders[index - 1];
                      handleColumnMappingChange(field.key, value);
                    }}
                    multiselect={false}
                  />
                </div>
              ))}
            </div>

            <div className={style.preview}>
              <p>Preview: {parsedData.length} records found</p>
            </div>

            {error && csvHeaders.length > 0 && (
              <div className={style.errorMessage}>{error}</div>
            )}

            <div className={style.actions}>
              <Button onClick={onClose} variants={["text"]}>Cancel</Button>
              <Button onClick={handleUpload} disabled={uploading}>
                {uploading ? "Uploading..." : "Upload & Categorize"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
};

export default UploadDialog;

